// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Squirm — bootstrap: screens, solo-vs-bots, and the full P2P wiring.
import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx, type Patch } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import {
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
  clearRoomInUrl,
  type Lobby,
} from '@ben-gy/game-engine/lobby';
import { shuffle, makeRng } from '@ben-gy/game-engine/rng';
import {
  Session,
  GAME_CHANNELS,
  type Action,
  type Transport,
  type HandAttest,
} from './session';
import type { PublicState, PrivateView } from './game';
import { MODES, MODE_IDS, DEFAULT_MODE, modeOf } from './modes';
import { PROFILES, type BotProfile } from './bot';
import { GameUi, resultsBodyHtml, type GameMeta } from './ui';
import { createCountdown, type Countdown } from './countdown';
import { setReducedMotion } from './fx';
import { critterSvg } from './critters';

const SLUG = 'squirm';
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

// ---- custom SFX patches ----------------------------------------------------
const PATCHES: Record<string, Patch> = {
  blip: { type: 'sine', freq: [480, 300], dur: 0.12, gain: 0.18 },
  select: { type: 'triangle', freq: [420, 600], dur: 0.07, gain: 0.18 },
  coin: { type: 'triangle', freq: [660, 990], dur: 0.16, gain: 0.22 },
  hit: { type: 'sawtooth', freq: [260, 70], dur: 0.22, gain: 0.28, noise: true },
  win: { type: 'triangle', freq: [523, 1046], dur: 0.5, gain: 0.26 },
  lose: { type: 'sawtooth', freq: [320, 90], dur: 0.55, gain: 0.3 },
  powerup: { type: 'square', freq: [220, 440], dur: 0.24, gain: 0.2 },
};

const store = createStore(SLUG);
const sfx = createSfx({ muted: store.get('muted', false) as boolean, patches: PATCHES });
const reducedMotion =
  (store.get('reducedMotion', null) as boolean | null) ??
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
setReducedMotion(reducedMotion);

const app = document.getElementById('app')!;
app.innerHTML = `<div class="main-content"><div id="screen"></div></div>
  <footer class="site-footer">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> ·
  <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a></footer>`;
const screen = document.getElementById('screen')!;

hardenViewport();

// ---- shared state ----------------------------------------------------------
let net: Net | null = null;
let rounds: Rounds<{ mode: string }> | null = null;
let lobby: Lobby | null = null;
let session: Session | null = null;
let gameUi: GameUi | null = null;
let countdown: Countdown | null = null;
let roomCode = '';
let modeId = (store.get('mode', DEFAULT_MODE) as string) || DEFAULT_MODE;
let pendingRoom: string | null = null;
let latestPub: PublicState | null = null;
let latestPriv: PrivateView | null = null;
let soloRound = 0;
const matchWins = new Map<string, number>();
let lastNames = new Map<string, string>();
let paused = false;

const turnReady: Promise<void> = getTurnConfig()
  .then((c) => setTurnConfig(c))
  .catch(() => {});

/** A distinct default handle per device so two peers are never both "You". */
const HANDLES = [
  'Fox', 'Wren', 'Moth', 'Vole', 'Newt', 'Lynx', 'Hare', 'Crow', 'Toad', 'Mole',
  'Finch', 'Stoat', 'Otter', 'Raven', 'Shrew', 'Heron', 'Sable', 'Quail',
];
function playerName(): string {
  const stored = store.get('name', '') as string;
  if (stored) return stored;
  const gen = `${HANDLES[Math.floor(Math.random() * HANDLES.length)]}-${Math.floor(Math.random() * 90 + 10)}`;
  store.set('name', gen);
  return gen;
}

// ---- boot ------------------------------------------------------------------
const url = new URL(location.href);
const roomParam = url.searchParams.get('room');
if (roomParam) pendingRoom = normalizeRoomCode(roomParam);
if (!store.get('seenHelp', false)) {
  showHowTo(true);
} else if (pendingRoom) {
  playFriends();
} else {
  showMenu();
}

// ---- menu ------------------------------------------------------------------
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function showMenu(): void {
  teardownAll();
  setPlaying(false);
  matchWins.clear();
  screen.innerHTML = `
    <div class="menu">
      <div class="brand">
        <div class="brand-critters">${['spider', 'roach', 'fly', 'grub', 'rat', 'bat']
          .map((t) => critterSvg(t as never, { size: 34 }))
          .join('')}</div>
        <h1>Squirm</h1>
        <p class="tag">Slide a critter face-down, say what it is — and lie if you dare.</p>
      </div>
      <div class="mode-pick" role="radiogroup" aria-label="Mode">${modeCards()}</div>
      <div class="menu-btns">
        <button class="btn big primary" data-act="solo">Play solo</button>
        <button class="btn big" data-act="friends">Play with friends</button>
      </div>
      <div class="menu-links">
        <button class="btn ghost" data-act="howto">How to play</button>
        <button class="btn ghost" data-act="mute">${sfx.muted() ? '🔇 Sound off' : '🔊 Sound on'}</button>
      </div>
    </div>`;
  screen.querySelector('[data-act="solo"]')!.addEventListener('click', () => startSolo());
  screen.querySelector('[data-act="friends"]')!.addEventListener('click', () => playFriends());
  screen.querySelector('[data-act="howto"]')!.addEventListener('click', () => showHowTo(false));
  screen.querySelector('[data-act="mute"]')!.addEventListener('click', (e) => {
    sfx.unlock();
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    (e.currentTarget as HTMLElement).textContent = sfx.muted() ? '🔇 Sound off' : '🔊 Sound on';
  });
  wireModeCards();
}

function modeCards(): string {
  return MODE_IDS.map((id) => {
    const m = MODES[id];
    return `<button class="mode-card${id === modeId ? ' on' : ''}" role="radio" aria-checked="${id === modeId}" data-mode="${id}">
      <span class="mc-name">${m.name}</span><span class="mc-blurb">${m.blurb}</span></button>`;
  }).join('');
}

function wireModeCards(): void {
  screen.querySelectorAll<HTMLElement>('.mode-card').forEach((b) =>
    b.addEventListener('click', () => {
      modeId = b.dataset.mode!;
      store.set('mode', modeId);
      screen.querySelectorAll('.mode-card').forEach((x) => {
        x.classList.toggle('on', x === b);
        x.setAttribute('aria-checked', String(x === b));
      });
      sfx.unlock();
      sfx.play('select');
    }),
  );
}

function showHowTo(firstTime: boolean): void {
  setPlaying(false);
  screen.innerHTML = `
    <div class="howto">
      <h2>How to play</h2>
      <ol>
        <li>On your turn, slide a card face-down to a rival and <b>name a critter</b> — the truth, or a lie.</li>
        <li>They must decide: <b>Believe</b> you, <b>call your bluff</b>, or <b>peek &amp; pass</b> it on with a lie of their own.</li>
        <li>Guess wrong and the card lands face-up in front of you.</li>
        <li><b>Collect four of one critter (three in Swarm) and you're out.</b> Last one un-buried wins.</li>
      </ol>
      <p class="howto-note">Read the player, not the card. Watch who's been caught lying — it's shown under each name. Peeks are limited, so spend them well.</p>
      <button class="btn big primary" data-act="ok">${firstTime ? 'Got it' : 'Back'}</button>
    </div>`;
  screen.querySelector('[data-act="ok"]')!.addEventListener('click', () => {
    store.set('seenHelp', true);
    if (firstTime && pendingRoom) playFriends();
    else showMenu();
  });
}

// ---- solo ------------------------------------------------------------------
function startSolo(): void {
  teardownAll();
  sfx.unlock();
  const seatCount = 4; // you + 3 bots — the sweet spot
  const seed = (Math.floor(Math.random() * 0xffffffff) as number) >>> 0;
  const rng = makeRng(seed);
  const profiles = shuffle(rng, [...PROFILES]).slice(0, seatCount - 1);
  const bots = new Map<string, BotProfile>();
  const specs = [{ id: 'you', name: 'You', bot: false }];
  profiles.forEach((p, i) => {
    const id = `bot${i}`;
    specs.push({ id, name: p.name, bot: true });
    bots.set(id, { ...p, id });
  });
  lastNames = new Map(specs.map((s) => [s.id, s.name]));
  soloRound += 1;

  session = new Session({
    selfId: 'you',
    solo: true,
    rules: modeOf(modeId).rules,
    bots,
    botDelayMs: reducedMotion ? 550 : 950,
    onPublic: handlePublic,
    onPrivate: handlePrivate,
  });
  mountGame(() => startSolo());
  runCountdown(() => session!.startAsHost(seed, specs));
}

// ---- multiplayer -----------------------------------------------------------
function playFriends(): void {
  void leaveRoom();
  if (pendingRoom) {
    const code = pendingRoom;
    pendingRoom = null;
    void openRoom(code, false);
    return;
  }
  setPlaying(false);
  createRoomEntry({
    container: screen,
    title: 'Play with friends',
    subtitle: 'Best with 3–6. Start a room, or type a friend’s code.',
    onSubmit: (code, created) => void openRoom(code, created),
    onCancel: () => showMenu(),
  });
}

async function openRoom(code: string, created: boolean): Promise<void> {
  await leaveRoom();
  await turnReady;
  setRoomInUrl(code);
  roomCode = code;
  matchWins.clear();

  try {
    net = createNet(
      { appId: roomAppId(SLUG), roomId: code, claimHost: created },
      {
        onHostChange: () => syncAuthority(),
        onPeerLeave: (id) => {
          session?.onPeerLeave(id);
          syncAuthority();
        },
        onPeers: () => {
          session?.onRoster();
          syncAuthority();
        },
      },
    );
  } catch {
    flash('Could not open that room — try again.');
    showMenu();
    return;
  }

  rounds = createRounds<{ mode: string }>({
    net,
    playerName: playerName(),
    minPlayers: MIN_PLAYERS,
    roundOpts: () => ({ mode: modeId }),
    onRound: ({ seed, players, isHost, opts }) => startTable(seed, players, isHost, opts),
  });

  showLobby();
}

function showLobby(): void {
  if (!net || !rounds) return;
  teardownGame();
  setPlaying(false);
  lobby = createLobby({
    container: screen,
    net,
    rounds,
    roomCode,
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    modeSlot: () => (net!.isHost() ? modePickerHtml() : modeNoteHtml()),
    onModeMount: () => wireLobbyModePicker(),
    onCancel: () => showMenu(),
  });
}

function modePickerHtml(): string {
  return `<div class="lobby-modes">${MODE_IDS.map(
    (id) =>
      `<button class="lm${id === modeId ? ' on' : ''}" data-mode="${id}">${MODES[id].name}</button>`,
  ).join('')}<div class="lm-blurb">${MODES[modeId].blurb}</div></div>`;
}
function modeNoteHtml(): string {
  const hostMode = (rounds?.state().hostOpts?.mode as string) ?? modeId;
  return `<div class="lobby-modes note">Host picked <b>${MODES[modeOf(hostMode).id].name}</b> — ${MODES[modeOf(hostMode).id].blurb}</div>`;
}
function wireLobbyModePicker(): void {
  screen.querySelectorAll<HTMLElement>('.lm').forEach((b) =>
    b.addEventListener('click', () => {
      modeId = b.dataset.mode!;
      store.set('mode', modeId);
      lobby?.repaint();
    }),
  );
}

function startTable(seed: number, players: RoundPlayer[], isHost: boolean, opts: { mode: string }): void {
  if (!net) return;
  teardownGame();
  lobby?.destroy();
  lobby = null;

  if (!players.some((p) => p.id === net!.selfId)) {
    showLobby();
    return;
  }
  lastNames = new Map(players.map((p) => [p.id, p.name]));

  const wire = wireRound(net);
  unwireRound = wire.off;
  const mode = modeOf(opts?.mode);

  session = new Session({
    selfId: net.selfId,
    solo: false,
    transport: wire.transport,
    roster: players.map((p) => p.id),
    rules: mode.rules,
    onPublic: handlePublic,
    onPrivate: handlePrivate,
    onFlash: (msg) => flash(msg),
    onStranded: () => {
      rounds?.finish();
      showLobby();
    },
  });
  mountGame(playAgain);

  if (isHost) {
    runCountdown(() => {
      session!.startAsHost(
        seed,
        players.map((p) => ({ id: p.id, name: p.name, bot: false })),
      );
      syncAuthority();
    });
  } else {
    session.start();
    runCountdown(() => {});
    syncAuthority();
  }
}

let unwireRound: (() => void) | null = null;
function wireRound(n: Net): { transport: Transport; off: () => void } {
  const sendSnap = n.channel<PublicState>(GAME_CHANNELS.snap, (pub) => session?.onSnapshot(pub));
  const sendPriv = n.channel<PrivateView>(GAME_CHANNELS.priv, (p) => session?.onPrivate(p));
  const sendAct = n.channel<Action>(GAME_CHANNELS.act, (a, from) => session?.onAction(from, a));
  const sendReq = n.channel<null>(GAME_CHANNELS.handRequest, (_d, from) => session?.onHandRequest(from));
  const sendGive = n.channel<HandAttest>(GAME_CHANNELS.handReply, (m, from) => session?.onHandReply(from, m));
  return {
    transport: {
      sendSnap: (pub) => sendSnap(pub),
      sendPriv: (to, priv) => sendPriv(priv, to),
      sendAct: (a) => sendAct(a),
      requestHands: () => sendReq(null),
      sendHand: (to, attest) => sendGive(attest, to),
    },
    off: () => {
      sendSnap.off();
      sendPriv.off();
      sendAct.off();
      sendReq.off();
      sendGive.off();
    },
  };
}

function syncAuthority(): void {
  if (!net || !session) return;
  if (!net.hostSettled()) return;
  session.setHost(session.authorityFor(net.peers(), net.host()) === net.selfId);
}

// ---- game screen -----------------------------------------------------------
function mountGame(onRestart: () => void): void {
  setPlaying(true);
  paused = false;
  latestPub = null;
  latestPriv = null;
  screen.innerHTML = `<div class="game"></div>`;
  const g = screen.querySelector('.game') as HTMLElement;
  gameUi = new GameUi(g, session!.selfId, sfx, {
    onPlay: (cardId, to, claim) => session?.act({ t: 'play', cardId, to, claim }),
    onGuess: (gs) => session?.act({ t: 'guess', g: gs }),
    onPeekStart: () => session?.act({ t: 'peekStart' }),
    onPeekPass: (to, claim) => session?.act({ t: 'peekPass', to, claim }),
    onPause: () => showPause(onRestart),
    onFeedback: (el) => window.feedback?.open({ returnFocusTo: el }),
  });
}

function runCountdown(onDone: () => void): void {
  countdown?.cancel();
  const g = (screen.querySelector('.game') as HTMLElement) ?? screen;
  countdown = createCountdown({ root: g, sfx, reducedMotion, onDone });
}

function meta(): GameMeta {
  const round = net && rounds ? rounds.state().round : soloRound;
  const tally = [...lastNames.entries()]
    .map(([id, name]) => ({ name, wins: matchWins.get(id) ?? 0, isSelf: id === session?.selfId }))
    .filter((t) => t.wins > 0);
  return { modeName: modeOf(modeId).name, round, tally };
}

function handlePublic(pub: PublicState): void {
  latestPub = pub;
  repaint();
}
function handlePrivate(priv: PrivateView | null): void {
  latestPriv = priv;
  repaint();
}

function repaint(): void {
  if (!latestPub || paused) return;
  if (latestPub.phase === 'over') {
    // Tally the winner once.
    if (!tallied) {
      tallied = true;
      const winner = latestPub.ranking[0];
      if (winner) matchWins.set(winner, (matchWins.get(winner) ?? 0) + 1);
    }
    showResults(latestPub);
    return;
  }
  tallied = false;
  if (!gameUi) return;
  gameUi.update(latestPub, latestPriv, meta());
}

let tallied = false;

// ---- results ---------------------------------------------------------------
function showResults(pub: PublicState): void {
  gameUi?.destroy();
  gameUi = null;
  setPlaying(false);
  countdown?.cancel();
  const solo = !net;
  screen.innerHTML = `
    <div class="results">
      ${resultsBodyHtml(pub, session!.selfId, meta())}
      <div class="res-actions">
        <button class="btn big primary" data-act="again">Play again</button>
        ${net ? '<button class="btn" data-act="lobby">Back to lobby</button>' : ''}
        <button class="btn" data-act="menu">Menu</button>
      </div>
      <div class="res-wait" aria-live="polite"></div>
      <button class="btn ghost small results-feedback" data-act="fb">Something feel off? Tell us</button>
    </div>`;
  screen.querySelector('[data-act="again"]')!.addEventListener('click', () => {
    if (solo) startSolo();
    else playAgain();
  });
  screen.querySelector('[data-act="menu"]')!.addEventListener('click', () => showMenu());
  screen.querySelector('[data-act="lobby"]')?.addEventListener('click', () => {
    rounds?.finish();
    showLobby();
  });
  screen.querySelector('[data-act="fb"]')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  if (net && rounds) wireRematchWait();
}

function playAgain(): void {
  if (!rounds) return;
  rounds.finish();
  rounds.vote();
  wireRematchWait();
}

let rematchTick: ReturnType<typeof setInterval> | null = null;
function wireRematchWait(): void {
  if (rematchTick) clearInterval(rematchTick);
  const paint = (): void => {
    const wait = screen.querySelector('.res-wait');
    if (!wait || !rounds) return;
    const st = rounds.state();
    if (!st.voted) {
      wait.innerHTML = '';
      return;
    }
    const secs = st.startsInMs != null ? Math.ceil(st.startsInMs / 1000) : null;
    const others = st.present.length - st.votes.length;
    wait.innerHTML = `<span class="spinner"></span> Ready — ${
      secs != null ? `starting in ${secs}s` : others > 0 ? `waiting for ${others} more…` : 'waiting…'
    }${net?.isHost() && st.canStart ? ' <button class="btn small" data-act="force">Start now</button>' : ''}`;
    wait.querySelector('[data-act="force"]')?.addEventListener('click', () => rounds!.go());
  };
  paint();
  rematchTick = setInterval(paint, 400);
}

// ---- pause -----------------------------------------------------------------
function showPause(onRestart: () => void): void {
  paused = true;
  session?.stop();
  const solo = !net;
  const overlay = document.createElement('div');
  overlay.className = 'pause-overlay';
  overlay.innerHTML = `<div class="pause-card">
    <h2>Paused</h2>
    <button class="btn big primary" data-act="resume">Resume</button>
    <button class="btn" data-act="mute">${sfx.muted() ? '🔇 Sound off' : '🔊 Sound on'}</button>
    ${solo ? '<button class="btn" data-act="restart">Restart</button>' : ''}
    <button class="btn ghost" data-act="menu">${solo ? 'Back to menu' : 'Leave room'}</button>
  </div>`;
  screen.appendChild(overlay);
  const close = (): void => {
    paused = false;
    overlay.remove();
    session?.start();
    repaint();
  };
  overlay.querySelector('[data-act="resume"]')!.addEventListener('click', close);
  overlay.querySelector('[data-act="mute"]')!.addEventListener('click', (e) => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    (e.currentTarget as HTMLElement).textContent = sfx.muted() ? '🔇 Sound off' : '🔊 Sound on';
  });
  overlay.querySelector('[data-act="restart"]')?.addEventListener('click', () => {
    overlay.remove();
    onRestart();
  });
  overlay.querySelector('[data-act="menu"]')!.addEventListener('click', () => {
    overlay.remove();
    showMenu();
  });
}

// ---- flash / teardown ------------------------------------------------------
function flash(msg: string): void {
  const f = document.createElement('div');
  f.className = 'flash';
  f.textContent = msg;
  app.appendChild(f);
  setTimeout(() => f.remove(), 2600);
}

function teardownGame(): void {
  countdown?.cancel();
  countdown = null;
  gameUi?.destroy();
  gameUi = null;
  session?.destroy();
  session = null;
  if (rematchTick) clearInterval(rematchTick);
  rematchTick = null;
  unwireRound?.();
  unwireRound = null;
  tallied = false;
}

async function leaveRoom(): Promise<void> {
  teardownGame();
  lobby?.destroy();
  lobby = null;
  rounds?.destroy();
  rounds = null;
  if (net) {
    const n = net;
    net = null;
    clearRoomInUrl();
    await n.leave();
  }
}

function teardownAll(): void {
  void leaveRoom();
}

// Ensure the room is released if the tab closes.
window.addEventListener('beforeunload', () => {
  net?.leave();
});
